import React, { useEffect, useState } from 'react';
import { Globe, Github, FileText, Twitter, MessageCircle, Cookie, Copy, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { AgentCardProps } from '../types/index';
import HoverCard from './HoverCard';
import { useNavigate } from 'react-router-dom';
import {
    formatMarketCap,
    getTokenUtilityLevel,
    getAutonomyLevelColor,
    getAutonomyBarHeight,
    formatTimeAgo
} from '../utils/format';
import GitHubStatsCard from './GitHubStatsCard';
import axios from 'axios';

const AgentCard: React.FC<AgentCardProps> = ({
    id,
    name,
    description,
    marketCap,
    level,
    token_utility,
    openSource,
    framework,
    tags,
    website_url,
    github_url,
    documentation_url,
    x_url,
    discord_url,
    agent_team_status,
    telegram_url,
    cookie_url,
    agent_symbol,
    contract_addresses,
    icon_url,
    bullet_points, 
}) => {
    const navigate = useNavigate();
    const [lastUpdate, setLastUpdate] = useState<string | null>(null);
    const [showAllAddresses, setShowAllAddresses] = useState(false);
    const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

    const tokenUtilityLevel = getTokenUtilityLevel(token_utility);

    useEffect(() => {
        const fetchGitHubStats = async () => {
            if (openSource && github_url) {
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_BASE_URL}/agents/${id}/github`
                    );
                    setLastUpdate(response.data.last_github_update);
                } catch (err) {
                    console.error('Error fetching GitHub stats:', err);
                }
            }
        };

        fetchGitHubStats();
    }, [id, openSource, github_url]);

    const handleCardClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('.social-links')) {
            e.stopPropagation();
            return;
        }
        navigate(`/agent/${id}`, {
            state: {
                id,
                name,
                description,
                marketCap,
                level,
                token_utility,
                openSource,
                framework,
                tags,
                website_url,
                github_url,
                documentation_url,
                x_url,
                discord_url,
                agent_team_status,
                agent_symbol,
                icon_url,
                telegram_url,
                cookie_url,
                contract_addresses,
                bullet_points
            }
        });
    };

    // Function to get autonomy level text
    const getAutonomyLevelText = (level: number): string => {
        switch (level) {
            case 1:
                return 'LOW';
            case 2:
                return 'MEDIUM';
            case 3:
                return 'HIGH';
            case 4:
                return 'FULL';
            default:
                return 'LOW';
        }
    };

    const autonomyContent = (level: number) => {
        const getBarColor = (barLevel: number) => {
            // Only return color if it's the current level, otherwise gray
            if (level === barLevel) {
                switch (barLevel) {
                    case 1: return 'bg-[#ef4444]';  // Red for Low
                    case 2: return 'bg-[#f59e0b]';  // Orange for Medium
                    case 3: return 'bg-[#22c55e]';  // Green for High
                    case 4: return 'bg-[#bf00ff]';  // Purple for Full
                    default: return 'bg-gray-800';
                }
            }
            return 'bg-gray-800'; // Gray for inactive bars
        };
        const getBarHeight = (barLevel: number) => {
            switch (barLevel) {
                case 1:
                    return 'h-16'; // 25%
                case 2:
                    return 'h-24'; // 50%
                case 3:
                    return 'h-32'; // 75%
                case 4:
                    return 'h-40'; // 100%
                default:
                    return 'h-16';
            }
        };

        return (
            <div className="w-full md:w-[800px] p-8 ">
                <h3 className="text-2xl font-medium text-white mb-8">Check how autonomous an agent is?</h3>

                <div className="flex md:flex-row flex-col justify-between gap-16">
                    {/* Left side: Autonomy Levels List */}
                    <div className="space-y-6 flex-1">
                        <div className="space-y-1">
                            <div className="flex items-center gap-4">
                                <h4 className="text-lg font-medium text-white">Fully autonomous</h4>
                                <span className={`px-4 py-1 rounded-md text-sm ${level === 4 ? 'bg-[#bf00ff]' : 'bg-gradient-to-r from-[#bf00ff]/30 to-[#bf00ff]/20'}`}>
                                    Full
                                </span>
                            </div>
                            <p className="text-gray-400 text-sm">Operates and makes decisions completely independently without human input</p>
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center gap-4">
                                <h4 className="text-lg font-medium text-white">High autonomous</h4>
                                <span className={`px-4 py-1 rounded-md text-sm ${level === 3 ? 'bg-[#22c55e]' : 'bg-gradient-to-r from-[#22c55e]/30 to-[#22c55e]/20'}`}>
                                    High
                                </span>
                            </div>
                            <p className="text-gray-400 text-sm">Functions independently with minimal human oversight.</p>
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center gap-4">
                                <h4 className="text-lg font-medium text-white">Medium autonomous</h4>
                                <span className={`px-4 py-1 rounded-md text-sm ${level === 2 ? 'bg-[#f59e0b]' : 'bg-gradient-to-r from-[#f59e0b]/30 to-[#f59e0b]/20'}`}>
                                    Medium
                                </span>
                            </div>
                            <p className="text-gray-400 text-sm">Performs basic tasks alone but needs human guidance for complex decisions.</p>
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center gap-4">
                                <h4 className="text-lg font-medium text-white">Low autonomous</h4>
                                <span className={`px-4 py-1 rounded-md text-sm ${level === 1 ? 'bg-[#ef4444]' : 'bg-gradient-to-r from-[#ef4444]/30 to-[#ef4444]/20'}`}>
                                    Low
                                </span>
                            </div>
                            <p className="text-gray-400 text-sm">Requires constant human supervision and direction.</p>
                        </div>
                    </div>


                    {/* Right side: Bar Chart */}
                    <div className="flex-1 mt-8">
                        <div className="flex flex-col gap-6">

                            <div className="grid grid-cols-4 gap-6 h-full">
                                {[1, 2, 3, 4].map((barLevel) => (
                                    <div key={barLevel} className="flex items-end justify-center h-full">
                                        <div
                                            className={`w-8 rounded-md transition-all duration-300 ${level >= barLevel
                                                ? `${getBarColor(barLevel)} ${getBarHeight(barLevel)}`
                                                : `bg-gray-800 ${getBarHeight(barLevel)}`
                                                }`}
                                        />
                                    </div>
                                ))}
                            </div>



                            {/* Bar labels using the same grid */}
                            <div className="grid grid-cols-4 gap-6">
                                {['Low', 'Medium', 'High', 'Full'].map((label, index) => (
                                    <div key={label} className="text-center">
                                        <span className={`text-sm ${level === index + 1 ? 'text-white font-medium' : 'text-gray-600'
                                            }`}>
                                            {label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <h3 className="text-xl font-medium text-center mt-6 text-white">
                            Level of Autonomy
                        </h3>
                    </div>

                </div>
            </div>
        );
    };



    const tokenUtilityContent = (utility: string) => (
        <div>
            <h3 className="text-white font-medium mb-2">Token Utility Details</h3>
            <p className="text-gray-300 mb-2">{utility}</p>
            <div className="flex items-center gap-2 mt-2">
                <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                        <div
                            key={i}
                            className={`w-2 h-6 rounded-sm ${i < tokenUtilityLevel ? 'bg-green-500' : 'bg-gray-700'
                                }`}
                        />
                    ))}
                </div>
                <span className="text-gray-300 text-sm">
                    Level {tokenUtilityLevel}/5 Utility Score
                </span>
            </div>
        </div>
    );

    const marketCapContent = (value: string | number) => (
        <div>
            <h3 className="text-white font-medium mb-2">Market Capitalization</h3>
            <p className="text-gray-300 mb-2">Current Value: {formatMarketCap(value)}</p>
            <div className="text-sm text-gray-300">
                <p className="mb-1">Market Cap Categories:</p>
                <ul className="space-y-1">
                    <li>• Small Cap: Under $100M</li>
                    <li>• Mid Cap: $100M - $1B</li>
                    <li>• Large Cap: Over $1B</li>
                </ul>
            </div>
        </div>
    );

    const teamStatusContent = (status: 'DOXXED' | 'ANON') => (
        <div>
            <h3 className="text-white font-medium mb-2">Team Status: {status}</h3>
            <p className="text-gray-300">
                {status === 'DOXXED'
                    ? "The team members have publicly revealed their real identities and backgrounds. This typically indicates higher accountability and transparency, as team members can be verified and held responsible for the project's development."
                    : "The team members maintain their anonymity, which is common in the crypto space. While this preserves privacy and is a valid choice in decentralized projects, it requires additional due diligence from users as team members' backgrounds cannot be directly verified."
                }
            </p>
        </div>
    );

    // Function to parse contract addresses
    const parseContractAddresses = () => {
        if (!contract_addresses) return [];
        return contract_addresses.map(addr => {
            try {
                return JSON.parse(addr);
            } catch (e) {
                console.error('Error parsing contract address:', e);
                return null;
            }
        }).filter(Boolean);
    };

    const parsedAddresses = parseContractAddresses();

    const getChainExplorerUrl = (chain: string, address: string) => {
        const chainMap: { [key: string]: string } = {
            'Base': 'https://basescan.org/address/',
            'Ethereum': 'https://etherscan.io/address/',
            'Arbitrum': 'https://arbiscan.io/address/',
            'Polygon': 'https://polygonscan.com/address/',
            'Solana': 'https://solscan.io/address/',
            // Add more chains as needed
        };
        return chainMap[chain] ? `${chainMap[chain]}${address}` : '#';
    };

    const handleCopyAddress = (address: string) => {
        navigator.clipboard.writeText(address);
        setCopiedAddress(address);
        setTimeout(() => setCopiedAddress(null), 2000);
    };

    return (
        <div
            onClick={handleCardClick}
            className="bg-[#1A1721]/50 rounded-xl p-6 hover:bg-[#1A1721]/70 transition-all border border-gray-800 cursor-pointer"
        >
            <div className="grid grid-cols-1 md:grid-cols-7 gap-4 md:gap-6 md:items-center">
                {/* Name, Symbol, and Contract Address section */}
                <div className="col-span-1 md:col-span-2">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-3">
                            {/* Agent Icon */}
                            {icon_url ? (
                                <img
                                    src={icon_url}
                                    alt={`${name} icon`}
                                    className="w-10 h-10 rounded-xl object-cover bg-gray-800/50 border border-gray-700/50"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="%236b7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Crect width="18" height="18" x="3" y="3" rx="2" ry="2"%3E%3C/rect%3E%3Cpath d="M12 8v8"%3E%3C/path%3E%3Cpath d="M8 12h8"%3E%3C/path%3E%3C/svg%3E';
                                        target.className = "w-10 h-10 rounded-xl p-2 bg-gray-800/50 border border-gray-700/50";
                                    }}
                                />
                            ) : (
                                <div className="w-10 h-10 rounded-xl bg-gray-800/50 border border-gray-700/50 flex items-center justify-center">
                                    {/* <span className="text-gray-400 text-xl">♦</span> */}
                                </div>
                            )}
                            <div className="flex flex-col">
                                <span className="text-white font-medium text-lg">{name}</span>


                                <div className="flex items-center gap-2">
                                    {agent_symbol && parsedAddresses.length > 0 && (
                                        <>
                                            <div className="flex flex-col relative">
                                                <div className="flex items-center gap-2">
                                                    <a
                                                        href={getChainExplorerUrl(parsedAddresses[0].chain, parsedAddresses[0].address)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-400 hover:text-blue-300 hover:underline transition-colors text-sm flex items-center gap-1"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                    {`${agent_symbol} ${parsedAddresses.length > 2 ? `(${parsedAddresses[0].chain})` : ''}`}
                                                        <ExternalLink className="w-3 h-3" />
                                                    </a>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCopyAddress(parsedAddresses[0].address);
                                                        }}
                                                        className="p-1 hover:bg-gray-700/50 rounded-md transition-colors"
                                                    >
                                                        <Copy className={`w-3 h-3 ${copiedAddress === parsedAddresses[0].address ? 'text-green-500' : 'text-gray-400'}`} />
                                                    </button>
                                                    {parsedAddresses.length > 1 && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setShowAllAddresses(!showAllAddresses);
                                                            }}
                                                            className="p-1 hover:bg-gray-700/50 rounded-md transition-colors"
                                                        >
                                                            {showAllAddresses ? (
                                                                <ChevronUp className="w-3 h-3 text-gray-400" />
                                                            ) : (
                                                                <ChevronDown className="w-3 h-3 text-gray-400" />
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                                {showAllAddresses && parsedAddresses.length > 1 && (
                                                    <div className="absolute top-full left-0 mt-1 bg-gray-800 rounded-md shadow-lg z-10 min-w-max">
                                                        {parsedAddresses.slice(1).map((addr, index) => (
                                                            <div key={index} className="flex items-center gap-2 p-2 hover:bg-gray-700">
                                                                <a
                                                                    href={getChainExplorerUrl(addr.chain, addr.address)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-blue-400 hover:text-blue-300 hover:underline transition-colors text-sm flex items-center gap-1"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    {`${agent_symbol} (${addr.chain})`}
                                                                    <ExternalLink className="w-3 h-3" />
                                                                </a>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleCopyAddress(addr.address);
                                                                    }}
                                                                    className="p-1 hover:bg-gray-700/50 rounded-md transition-colors"
                                                                >
                                                                    <Copy className={`w-3 h-3 ${copiedAddress === addr.address ? 'text-green-500' : 'text-gray-400'}`} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>



                        <div className="flex flex-wrap items-center py-1 gap-2 social-links">
                            {website_url && (
                                <a
                                    href={website_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:bg-gray-700/50 p-1 rounded-full transition-colors"
                                    title="Website"
                                >
                                    <Globe className="w-4 h-4 text-gray-400 hover:text-white transition-colors" />
                                </a>
                            )}
                            {documentation_url && (
                                <a
                                    href={documentation_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:bg-gray-700/50 p-1 rounded-full transition-colors"
                                    title="Documentation"
                                >
                                    <FileText className="w-4 h-4 text-gray-400 hover:text-white transition-colors" />
                                </a>
                            )}
                            {x_url && (
                                <a
                                    href={x_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:bg-gray-700/50 p-1 rounded-full transition-colors"
                                    title="Twitter"
                                >
                                    <Twitter className="w-4 h-4 text-gray-400 hover:text-white transition-colors" />
                                </a>
                            )}
                            {discord_url && (
                                <a
                                    href={discord_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:bg-gray-700/50 p-1 rounded-full transition-colors"
                                    title="Discord"
                                >
                                    <MessageCircle className="w-4 h-4 text-gray-400 hover:text-white transition-colors" />
                                </a>
                            )}
                            {github_url && (
                                <a
                                    href={github_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:bg-gray-700/50 p-1 rounded-full transition-colors"
                                    title="GitHub"
                                >
                                    <Github className="w-4 h-4 text-gray-400 hover:text-white transition-colors" />
                                </a>
                            )}
                            {telegram_url && (
                                <a
                                    href={telegram_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-full transition-colors"
                                >
                                    <MessageCircle className="w-5 h-5" />
                                </a>
                            )}
                            {cookie_url && (
                                <a
                                    href={cookie_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                                >
                                    <Cookie className="w-5 h-5" />
                                </a>
                            )}
                        </div>
                        <div className="flex flex-col">
                            <HoverCard content={
                                <div>
                                    <h3 className="text-white font-medium mb-2">Project Description</h3>
                                    <p className="text-gray-300 whitespace-pre-wrap">{description}</p>
                                </div>
                            }>
                                <p className="text-sm text-gray-400 line-clamp-2 md:line-clamp-2 cursor-help">
                                    {description}
                                </p>
                            </HoverCard>
                        </div>
                    </div>
                </div>

                {/* Level of Autonomy with Hover Card */}
                <div className="flex justify-between  items-center md:block">
                    <span className="text-gray-400 md:hidden">Level:</span>
                    <HoverCard content={autonomyContent(level)}>
                        <div className={`px-4 py-1.5 rounded-md text-sm font-medium ${level === 4 ? 'bg-[#bf00ff] text-white' :
                            level === 3 ? 'bg-[#22c55e] text-white' :
                                level === 2 ? 'bg-[#f59e0b] text-white' :
                                    'bg-[#ef4444] text-white'
                            }`}>
                            {getAutonomyLevelText(level)}
                        </div>
                    </HoverCard>
                </div>

                {/* Market Cap with Hover Card */}
                <div className="flex justify-between md:justify-start items-center md:block">
                    <span className="text-gray-400 md:hidden">Market Cap:</span>
                    <HoverCard content={marketCapContent(marketCap)}>
                        <div className="text-gray-200 font-medium">
                            {formatMarketCap(marketCap)}
                        </div>
                    </HoverCard>
                </div>

                {/* Token Utility with Hover Card */}
                <div className="flex justify-between md:justify-start items-center md:block">
                    <span className="text-gray-400 md:hidden">Token Utility:</span>
                    <HoverCard content={tokenUtilityContent(token_utility)}>
                        <div className="flex items-center gap-1 h-6">
                            {[1, 2, 3, 4, 5].map((bar) => (
                                <div
                                    key={bar}
                                    className={`w-2 h-full rounded-sm transition-all ${bar <= tokenUtilityLevel ? 'bg-green-500' : 'bg-gray-700'
                                        }`}
                                />
                            ))}
                        </div>
                    </HoverCard>
                </div>

                {/* Open Source Status with GitHub Stats */}
                <div className="flex justify-between md:justify-start items-center md:block">
                    <span className="text-gray-400 md:hidden">Open Source:</span>
                    {openSource ? (
                        <HoverCard content={
                            github_url ? (
                                <GitHubStatsCard agentId={id.toString()} />
                            ) : (
                                <div>
                                    <h3 className="text-white font-medium mb-2">Open Source Project</h3>
                                    <p className="text-gray-300">
                                        {lastUpdate
                                            ? `Last updated ${formatTimeAgo(lastUpdate)}`
                                            : "This project's code is publicly available but update time is unknown."
                                        }
                                    </p>
                                </div>
                            )
                        }>
                            <div className="text-gray-200 cursor-help">
                                {lastUpdate
                                    ? `Updated ${formatTimeAgo(lastUpdate)}`
                                    : 'Open Source'
                                }
                            </div>
                        </HoverCard>
                    ) : (
                        <div className="text-gray-200">Closed Source</div>
                    )}
                </div>

                {/* Framework with Hover Card */}
                <div className="flex justify-between md:justify-start items-center md:block">
                    <span className="text-gray-400 md:hidden">Framework:</span>
                    <HoverCard content={
                        <div>
                            <h3 className="text-white font-medium mb-2">Framework Details</h3>
                            <p className="text-gray-300">
                                Primary AI framework: {framework}
                            </p>
                        </div>
                    }>
                        <div className="text-gray-200">{framework}</div>
                    </HoverCard>
                </div>
            </div>

            {/* Tags - Full width and wrap on mobile */}
            <div className="flex flex-wrap gap-2 mt-4">
                {/* Team Status Pill */}
                <HoverCard content={teamStatusContent(agent_team_status || 'ANON')}>
                    <span
                        className={`px-2.5 py-1 text-xs rounded-md cursor-help ${
                            agent_team_status === 'DOXXED'
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                        }`}
                    >
                        {agent_team_status === 'DOXXED' ? 'Doxxed' : 'Anon'}
                    </span>
                </HoverCard>

                {/* Chain Pills */}
                {parsedAddresses.length > 0 && parsedAddresses.map((addr, index) => (
                    <span 
                        key={index}
                        className="px-2.5 py-1 text-xs bg-gray-800/50 text-gray-300 rounded-md border border-gray-700/30"
                    >
                        {addr.chain}
                    </span>
                ))}

                {/* Existing Tags */}
                {tags.map((tag, index) => (
                    <span
                        key={index}
                        className="px-2.5 py-1 text-xs bg-gray-800/50 text-gray-300 rounded-md"
                    >
                        {tag}
                    </span>
                ))}
            </div>
        </div >
    );
};

export default AgentCard; 